import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Alert } from '../../components/ui/alert';
import { CreditCard, AlertTriangle } from 'lucide-react';
import { PageHeader, LoadingSpinner, ConfirmDialog, CancellationReasonForm, LoadingButton } from '../../components/common';
import { PlanCard } from '../../features/subscription/components';
import { UpgradeBanner } from '../../features/dashboard/components';
import {
  useSubscription,
  useCreateCheckout,
  useChangeSubscription,
  useCancelSubscription,
  useBillingPortal,
} from '../../features/subscription/hooks/useSubscription';
import { useAuthStore } from '../../stores/authStore';
import { PlanType, BillingCycle } from '../../types';
import { PLAN_INFO } from '../../config/constants';
import { formatDate, getTrialRemainingDays } from '../../lib/utils';
import { usePageTitle } from '../../hooks/usePageTitle';
import { getErrorMessageByCode } from '../../config/errorMessages';
import { useQueryClient } from '@tanstack/react-query';
import { authService } from '../../services/authService';
import { showToast } from '../../stores/uiStore';

const plans: PlanType[] = ['starter', 'pro', 'business'];

const PLAN_ORDER = ['trial', 'starter', 'pro', 'business'] as const;
const isDowngrade = (from: PlanType, to: PlanType): boolean => {
  return PLAN_ORDER.indexOf(to) < PLAN_ORDER.indexOf(from);
};

export const SubscriptionPage: React.FC = () => {
  usePageTitle('プラン・お支払い');
  const { user, updateUser } = useAuthStore();
  const queryClient = useQueryClient();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  const trialRemainingDays = React.useMemo(() => {
    return user?.trial_ends_at
      ? getTrialRemainingDays(user.trial_ends_at)
      : null;
  }, [user?.trial_ends_at]);
  const [cancellationReasonModalOpen, setCancellationReasonModalOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReasonCategory, setCancelReasonCategory] = useState('');
  const [cancelReasonDetail, setCancelReasonDetail] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);
  const [changeDialogOpen, setChangeDialogOpen] = useState(false);
  const [isDowngrading, setIsDowngrading] = useState(false);

  const { data, isLoading, isError, refetch } = useSubscription();
  const location = useLocation();
  useEffect(() => {
    if (location.hash === '#api-keys' && !isLoading) {
      const el = document.getElementById('api-keys');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash, isLoading]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('success') === 'true') {
      window.history.replaceState({}, document.title, location.pathname);
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
      authService.getProfile().then((res) => {
        if (res.data) updateUser(res.data);
      });
      showToast.success('プランのお申し込みが完了しました');
    }
  }, [location.search]);
  const createCheckoutMutation = useCreateCheckout();
  const changeSubscriptionMutation = useChangeSubscription();
  const cancelSubscriptionMutation = useCancelSubscription();
  const billingPortalMutation = useBillingPortal();

  const subscription = data?.data;
  const currentPlan = subscription?.plan || user?.plan || 'trial';

  const msgUsed = subscription?.usage?.messages_this_month ?? 0;
  const msgLimit = subscription?.limits?.monthly_messages ?? null;
  const msgLimitType = subscription?.usage?.messages_limit_type ?? null;
  const msgPct = msgLimit && msgLimit > 0 ? Math.min(100, Math.round((msgUsed / msgLimit) * 100)) : 0;

  const handlePlanSelect = (plan: PlanType) => {
    if (plan === currentPlan) return;

    if (currentPlan === 'trial') {
      createCheckoutMutation.mutate({ plan, billingCycle });
    } else {
      setSelectedPlan(plan);
      setIsDowngrading(isDowngrade(currentPlan as PlanType, plan));
      setChangeDialogOpen(true);
    }
  };

  const handleChangePlanConfirm = () => {
    if (selectedPlan) {
      changeSubscriptionMutation.mutate(selectedPlan, {
        onSuccess: () => {
          setChangeDialogOpen(false);
          setSelectedPlan(null);
          setIsDowngrading(false);
        },
      });
    }
  };

  const handleCancelConfirm = () => {
    cancelSubscriptionMutation.mutate(
      { reason_category: cancelReasonCategory || undefined, reason_detail: cancelReasonDetail || undefined },
      {
        onSuccess: () => {
          setCancelDialogOpen(false);
          setCancelReasonCategory('');
          setCancelReasonDetail('');
        },
      }
    );
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setCancelReasonCategory('');
    setCancelReasonDetail('');
  };

  const handleBillingPortal = () => {
    billingPortalMutation.mutate();
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return (
      <div>
        <PageHeader title="プラン管理" subtitle="プランを選択" />
        <div className="text-center py-16">
          <p className="text-sm text-text-secondary mb-4">{getErrorMessageByCode('UNKNOWN')}</p>
          <button
            data-testid="error-retry-button"
            onClick={() => refetch()}
            className="text-sm px-4 py-2 border border-border rounded-md hover:bg-gray-50"
          >
            再試行
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="プラン"
        subtitle="ご利用中のプランと請求情報を管理します。"
      />

      {currentPlan === 'trial' && msgLimit != null && msgUsed >= msgLimit && (
        <UpgradeBanner
          suggestion="今月のメッセージ上限に達しました。プランをアップグレードして制限を解除しましょう。"
          onDismiss={() => {}}
        />
      )}

      {/* 現在のプランバナー */}
      <div
        className="rounded-[12px] p-5 mb-6"
        style={{ background: 'linear-gradient(135deg, #F0FDFA, #CCFBF1)' }}
        data-testid="current-plan-banner"
      >
        {currentPlan === 'trial' ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div>
                <p style={{ fontSize: '12px', color: '#64748B', marginBottom: '3px' }}>現在のプラン</p>
                <p style={{ fontSize: '22px', fontWeight: 700, color: '#0F172A' }} data-testid="current-plan-badge">無料トライアル</p>
              </div>
              {trialRemainingDays !== null && (
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '12px', color: '#64748B', marginBottom: '3px' }}>トライアル残り</p>
                  <p style={{ fontSize: '22px', fontWeight: 700, color: '#0a7a72' }} data-testid="trial-remaining-days">
                    あと{trialRemainingDays}日
                  </p>
                </div>
              )}
            </div>
            {user?.trial_ends_at && (
              <p style={{ fontSize: '12px', color: '#64748B', marginBottom: '10px' }} data-testid="trial-ends-at-display">
                {formatDate(user.trial_ends_at)}まで全機能をお試しいただけます
              </p>
            )}
            {trialRemainingDays !== null && (
              <div style={{ height: '6px', background: 'rgba(10,122,114,0.15)', borderRadius: '999px', overflow: 'hidden' }}>
                <div style={{ height: '100%', background: '#0a7a72', borderRadius: '999px', width: `${Math.min(100, Math.max(0, 100 - (trialRemainingDays / 14) * 100))}%`, transition: 'width 0.3s' }} />
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#0a7a72' }}
              >
                <CreditCard className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-text-secondary">現在のプラン</p>
                <p className="text-xl font-bold text-text-primary" data-testid="current-plan-badge">
                  {PLAN_INFO[currentPlan]?.name ?? currentPlan}
                </p>
                {PLAN_INFO[currentPlan]?.price_monthly != null && PLAN_INFO[currentPlan].price_monthly > 0 && (
                  <p className="text-sm text-text-secondary">
                    月額 ¥{PLAN_INFO[currentPlan].price_monthly.toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <LoadingButton
                variant="outline"
                size="sm"
                onClick={handleBillingPortal}
                loading={billingPortalMutation.isPending}
                className="bg-white/60"
              >
                <CreditCard className="h-4 w-4 mr-1" />
                支払い管理
              </LoadingButton>
              <Button
                variant="outline"
                size="sm"
                className="text-red-500 border-red-300 hover:bg-red-50 bg-white/60"
                onClick={() => setCancellationReasonModalOpen(true)}
              >
                解約
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* メッセージ使用量 — Phase25 EB-731 (enforce-limit-bar/billing-limit-line/messages-used-count/notification-banner) */}
      {msgLimit != null && (
        <div className="mb-6 p-4 rounded-lg border border-border bg-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-text-primary">今月のメッセージ数</span>
            <span data-testid="messages-used-count" className="text-sm font-semibold text-text-primary">
              {msgUsed.toLocaleString()} / {msgLimit.toLocaleString()}
            </span>
          </div>

          {/* enforce-limit-bar: trial → 赤の遮断バー */}
          {msgLimitType === 'enforce' && (
            <div data-testid="enforce-limit-bar" className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${msgPct}%`,
                  backgroundColor: msgPct >= 100 ? '#DC2626' : msgPct >= 80 ? '#F97316' : '#EF4444',
                }}
              />
            </div>
          )}

          {/* billing-limit-line: starter/pro → 課金ライン（遮断バーでない） */}
          {msgLimitType === 'billing' && (
            <div data-testid="billing-limit-line">
              <div className="relative w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-1">
                <div
                  className="absolute h-full bg-teal-400 rounded-full transition-all"
                  style={{ width: `${msgPct}%` }}
                />
              </div>
              <p className="text-xs text-text-secondary">
                超過分 ¥3/回答 は次フェーズより適用予定
              </p>
            </div>
          )}
        </div>
      )}

      {/* 決済失敗バナー */}
      {subscription?.status === 'past_due' && (
        <div
          data-testid="past-due-banner"
          style={{
            background: '#FEF2F2',
            border: '1.5px solid #FECACA',
            borderRadius: '12px',
            padding: '20px 24px',
            marginBottom: '24px',
          }}
        >
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '50%',
              background: '#FEE2E2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <AlertTriangle style={{ width: '20px', height: '20px', color: '#DC2626' }} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 700, fontSize: '16px', color: '#991B1B', marginBottom: '4px' }}>
                無料トライアルが終了しました
              </p>
              <p style={{ fontSize: '14px', color: '#B91C1C', lineHeight: 1.6, marginBottom: '14px' }}>
                チャットボットは現在停止中です。プランに加入するとすぐに再開できます。
              </p>
              <LoadingButton
                variant="outline"
                size="sm"
                className="border-red-400 text-red-700 hover:bg-red-50"
                onClick={handleBillingPortal}
                loading={billingPortalMutation.isPending}
                data-testid="past-due-banner-portal-button"
              >
                <CreditCard className="h-4 w-4 mr-1" />
                カード情報を更新
              </LoadingButton>
            </div>
          </div>
        </div>
      )}

      {/* 解約予定アラート */}
      {subscription?.cancel_at && (
        <Alert variant="warning" className="mb-6">
          {formatDate(subscription.cancel_at)}に解約予定です。
          この日までは現在のプランをご利用いただけます。
        </Alert>
      )}

      {/* プランを選ぶ + 月払い/年払い切替 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ fontSize: '18px', fontWeight: 700, color: '#0F172A' }}>
          {subscription?.status === 'past_due' ? 'プランを選んで再開する' : 'プランを選ぶ'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span
            className={`text-sm select-none ${billingCycle === 'monthly' ? 'text-text-primary font-medium' : 'text-text-secondary'}`}
          >
            月払い
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={billingCycle === 'yearly'}
            aria-label="年払いに切り替え"
            data-testid="billing-cycle-toggle"
            disabled={currentPlan === 'trial'}
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
            className="relative inline-flex flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              width: 44,
              height: 24,
              backgroundColor: billingCycle === 'yearly' ? '#0a7a72' : '#D1D5DB',
            }}
          >
            <span
              className="inline-block rounded-full bg-white shadow transition-transform duration-200"
              style={{
                width: 20,
                height: 20,
                marginTop: 2,
                transform: billingCycle === 'yearly' ? 'translateX(22px)' : 'translateX(2px)',
              }}
            />
          </button>
          <span
            className={`text-sm select-none ${billingCycle === 'yearly' ? 'text-text-primary font-medium' : 'text-text-secondary'}`}
          >
            年払い
          </span>
          <span
            className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#DCFCE7] text-[#166534]"
          >
            2ヶ月分お得
          </span>
        </div>
      </div>

      {/* プラン一覧 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {plans.map((plan) => (
          <div key={plan}>
            <PlanCard
              plan={plan}
              billingCycle={billingCycle}
              currentPlan={currentPlan}
              isLoading={createCheckoutMutation.isPending || changeSubscriptionMutation.isPending}
              onSelect={() => handlePlanSelect(plan)}
            />
          </div>
        ))}
      </div>

      <p style={{ fontSize: '12px', color: '#94A3B8', textAlign: 'center', marginTop: '24px', marginBottom: '24px' }}>
        料金はすべて税抜表示です。
      </p>

      {/* プラン変更確認ダイアログ */}
      <ConfirmDialog
        open={changeDialogOpen}
        onClose={() => { setChangeDialogOpen(false); setIsDowngrading(false); }}
        onConfirm={handleChangePlanConfirm}
        title="プランを変更"
        message={
          selectedPlan && isDowngrading ? (
            <>
              <p className="mb-2">
                {PLAN_INFO[selectedPlan].name}プランにダウングレードしますか？
              </p>
              <div data-testid="downgrade-warning" className="my-2">
                <p className="text-sm text-gray-500 mb-2">
                  以下の機能制限が適用されます：
                </p>
                <p className="text-sm">
                  ドメイン数: {PLAN_INFO[currentPlan as PlanType].domains} → {PLAN_INFO[selectedPlan].domains}
                </p>
                <p className="text-sm">
                  ストレージ: {PLAN_INFO[currentPlan as PlanType].storage_mb}MB → {PLAN_INFO[selectedPlan].storage_mb}MB
                </p>
                <p className="text-sm">
                  履歴保持: {PLAN_INFO[currentPlan as PlanType].chat_history_days}日 → {PLAN_INFO[selectedPlan].chat_history_days}日
                </p>
                {subscription?.usage?.domains_used !== undefined &&
                  subscription.usage.domains_used > PLAN_INFO[selectedPlan].domains && (
                  <p className="text-sm text-red-500 mt-1">
                    現在{subscription.usage.domains_used}ドメイン使用中。{PLAN_INFO[selectedPlan].name}プランの上限は{PLAN_INFO[selectedPlan].domains}ドメインです。
                  </p>
                )}
                {subscription?.usage?.storage_used_mb !== undefined &&
                  subscription.usage.storage_used_mb > PLAN_INFO[selectedPlan].storage_mb && (
                  <p className="text-sm text-red-500 mt-1">
                    現在のストレージ使用量が変更先プランの上限を超えています。
                  </p>
                )}
              </div>
              <p className="text-sm text-gray-500">
                変更は次回請求日から適用されます。
              </p>
            </>
          ) : (
            <>
              <p className="mb-2">
                {selectedPlan && `${PLAN_INFO[selectedPlan].name}プランに変更しますか？`}
              </p>
              <p className="text-sm text-gray-500">
                変更は次回請求日から適用されます。
              </p>
            </>
          )
        }
        confirmText="変更する"
        variant={isDowngrading ? 'danger' : 'info'}
        loading={changeSubscriptionMutation.isPending}
      />

      {/* 解約理由モーダル */}
      <CancellationReasonForm
        open={cancellationReasonModalOpen}
        onClose={() => setCancellationReasonModalOpen(false)}
        onConfirm={(category, detail) => {
          setCancelReasonCategory(category);
          setCancelReasonDetail(detail);
          setCancellationReasonModalOpen(false);
          setCancelDialogOpen(true);
        }}
      />

      {/* 解約確認ダイアログ */}
      <ConfirmDialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        onConfirm={handleCancelConfirm}
        title="サブスクリプションを解約"
        message={
          <>
            <p className="mb-2">
              サブスクリプションを解約してもよろしいですか？
            </p>
            <p className="text-sm text-gray-500">
              現在の請求期間終了まではご利用いただけます。
              解約後は無料トライアル相当の状態に戻ります。
            </p>
          </>
        }
        confirmText="解約する"
        variant="danger"
        loading={cancelSubscriptionMutation.isPending}
        confirmButtonTestId="cancellation-confirm-button"
        confirmDisabled={!cancelReasonCategory}
      />
    </div>
  );
};
